/**
 * Lógica compartida: pizza más vendida a partir de pedidos + catálogo.
 * Usada desde el dashboard (admin) al recalcular config/stats.
 */

const normalizeText = (value) => (value || "").toString().trim().toLowerCase();

const isPizzaCategory = (category) => {
  const normalized = normalizeText(category);
  if (!normalized) return false;
  if (normalized.startsWith("prepizza")) return false;
  return normalized === "pizza" || normalized === "pizzas" || normalized.startsWith("pizza ");
};

const isPizzaName = (name) => {
  const normalized = normalizeText(name);
  if (!normalized) return false;
  if (normalized.includes("prepizza")) return false;
  return normalized.includes("pizza");
};

/**
 * @param {Array<Record<string, unknown>>} orders
 * @param {Map<string, Record<string, unknown>>} productsMap
 * @param {Map<string, Record<string, unknown>>} productsByName
 * @returns {{ name: string; imageUrl: string | null; productId: string | null; count: number } | null}
 */
export const computeTopPizzaFromOrders = (orders, productsMap, productsByName) => {
  const totals = new Map();

  orders.forEach((order) => {
    const items = Array.isArray(order.items) ? order.items : [];
    items.forEach((item) => {
      const productId = item.productId || item.id || null;
      const nameKey = normalizeText(item.nombre || item.name);
      const product = productId
        ? productsMap.get(productId)
        : (nameKey ? productsByName.get(nameKey) : null);
      const category = product?.category || product?.categoria || "";

      if (product) {
        if (!isPizzaCategory(category)) return;
      } else if (!isPizzaName(item.nombre || item.name)) {
        return;
      }

      const quantity = Number(item.cantidad || item.quantity || 1) || 1;
      const key = productId || item.nombre || item.name;
      if (!key) return;

      const current = totals.get(key) || {
        key,
        count: 0,
        name: product?.name || product?.nombre || item.nombre || item.name || "Pizza",
        imageUrl: product?.imageUrl || product?.imagen || null,
        productId: productId || null,
      };

      current.count += quantity;
      totals.set(key, current);
    });
  });

  if (!totals.size) return null;

  const top = Array.from(totals.values()).sort((a, b) => {
    const aHasImage = Boolean(a.imageUrl);
    const bHasImage = Boolean(b.imageUrl);
    if (aHasImage !== bHasImage) return bHasImage ? 1 : -1;
    if (b.count !== a.count) return b.count - a.count;
    return a.name.localeCompare(b.name, "es");
  })[0];

  return {
    name: top.name,
    imageUrl: top.imageUrl || null,
    productId: top.productId || null,
    count: top.count,
  };
};

/**
 * @param {Array<Record<string, unknown>>} products
 */
export const buildProductMaps = (products) => {
  const productsMap = new Map(products.map((p) => [p.id, p]));
  const productsByName = new Map(
    products
      .map((p) => ({
        key: normalizeText(p.name || p.nombre),
        product: p,
      }))
      .filter((e) => e.key)
      .map((e) => [e.key, e.product])
  );
  return { productsMap, productsByName };
};
